import React, { useEffect, forwardRef } from "react";
import { useNavigate } from "react-router-dom";
import axios from "axios";

// import mui
import Box from "@mui/material/Box";
import { AuthContext } from "../components/ContextProvider";
import AddBox from "@material-ui/icons/AddBox";
import ArrowDownward from "@material-ui/icons/ArrowDownward";
import Check from "@material-ui/icons/Check";
import ChevronLeft from "@material-ui/icons/ChevronLeft";
import ChevronRight from "@material-ui/icons/ChevronRight";
import Clear from "@material-ui/icons/Clear";
import DeleteOutline from "@material-ui/icons/DeleteOutline";
import Edit from "@material-ui/icons/Edit";
import FilterList from "@material-ui/icons/FilterList";
import FirstPage from "@material-ui/icons/FirstPage";
import LastPage from "@material-ui/icons/LastPage";
import Remove from "@material-ui/icons/Remove";
import SaveAlt from "@material-ui/icons/SaveAlt";
import Search from "@material-ui/icons/Search";
import ViewColumn from "@material-ui/icons/ViewColumn";
import MaterialTable from "material-table";
import { ThemeProvider, createTheme } from "@mui/material";
import {
  Button,
  Menu,
  MenuItem,
  ListItemIcon,
  ListItemText,
} from "@mui/material";
import { PictureAsPdf, Description, TableChart } from "@mui/icons-material";

// Import document generation libraries
import jsPDF from "jspdf";
import "jspdf-autotable";
import {
  Document,
  Packer,
  Paragraph,
  TextRun,
  PageBreak,
  ImageRun,
  HeadingLevel,
} from "docx";
import { saveAs } from "file-saver";
import * as XLSX from "xlsx";

const tableIcons = {
  Add: forwardRef((props, ref) => <AddBox {...props} ref={ref} />),
  Check: forwardRef((props, ref) => <Check {...props} ref={ref} />),
  Clear: forwardRef((props, ref) => <Clear {...props} ref={ref} />),
  Delete: forwardRef((props, ref) => <DeleteOutline {...props} ref={ref} />),
  DetailPanel: forwardRef((props, ref) => (
    <ChevronRight {...props} ref={ref} />
  )),
  Edit: forwardRef((props, ref) => <Edit {...props} ref={ref} />),
  Export: forwardRef((props, ref) => <SaveAlt {...props} ref={ref} />),
  Filter: forwardRef((props, ref) => <FilterList {...props} ref={ref} />),
  FirstPage: forwardRef((props, ref) => <FirstPage {...props} ref={ref} />),
  LastPage: forwardRef((props, ref) => <LastPage {...props} ref={ref} />),
  NextPage: forwardRef((props, ref) => <ChevronRight {...props} ref={ref} />),
  PreviousPage: forwardRef((props, ref) => (
    <ChevronLeft {...props} ref={ref} />
  )),
  ResetSearch: forwardRef((props, ref) => <Clear {...props} ref={ref} />),
  Search: forwardRef((props, ref) => <Search {...props} ref={ref} />),
  SortArrow: forwardRef((props, ref) => <ArrowDownward {...props} ref={ref} />),
  ThirdStateCheck: forwardRef((props, ref) => <Remove {...props} ref={ref} />),
  ViewColumn: forwardRef((props, ref) => <ViewColumn {...props} ref={ref} />),
};

const mainUrl = "https://www.jas-vermittlung.de";
// const mainUrl = "http://localhost";

// Create an Axios instance with custom configurations
const api = axios.create({
  baseURL: mainUrl, // Replace with your API's base URL
  headers: {
    "Content-Type": "application/json", // Specify the content type
  },
});

const RegisteredPeople = () => {
  const defaultMaterialTheme = createTheme();
  const navigate = useNavigate();

  const { auth } = React.useContext(AuthContext);

  const [data, setData] = React.useState([]);
  const [loading, setLoading] = React.useState(false);
  const [exportAnchorEl, setExportAnchorEl] = React.useState(null);
  const [selectedRows, setSelectedRows] = React.useState([]);

  useEffect(() => {
    if (auth) {
      getRegisteredPeople();
    } else {
      navigate("/login");
    }
  }, []);

  const getRegisteredPeople = () => {
    // Make a GET request
    setLoading(true);
    api
      .get("/api/students.php")
      .then((response) => {
        // Handle the API response
        // console.log("API response:", response.data);
        setData(response.data);
        setLoading(false);
      })
      .catch((error) => {
        // Handle errors, including CORS-related issues
        console.error("API request failed:", error);
        setLoading(false);
      });
  };

  const deletePerson = (data) => {
    api
      .post("/api/students.php", data.id)
      .then((response) => {
        // Handle the API response
        if (response) {
          getRegisteredPeople();
        }
      })
      .catch((error) => {
        // Handle errors, including CORS-related issues
        console.error("API request failed:", error);
      });
  };

  // Export menu handlers
  const handleExportClick = (event) => {
    setExportAnchorEl(event.currentTarget);
  };

  const handleExportClose = () => {
    setExportAnchorEl(null);
  };

  // Format date for display
  const formatDate = (dateString) => {
    if (!dateString) return "";

    try {
      // Handle different date formats
      let date;

      // If it's already a valid date string, use it directly
      if (dateString instanceof Date) {
        date = dateString;
      } else if (typeof dateString === "string") {
        // Try to parse the date string
        // Handle MySQL date format (YYYY-MM-DD)
        if (dateString.match(/^\d{4}-\d{2}-\d{2}$/)) {
          date = new Date(dateString + "T00:00:00");
        }
        // Handle MySQL datetime format (YYYY-MM-DD HH:MM:SS)
        else if (dateString.match(/^\d{4}-\d{2}-\d{2} \d{2}:\d{2}:\d{2}$/)) {
          date = new Date(dateString.replace(" ", "T"));
        }
        // Handle other formats
        else {
          date = new Date(dateString);
        }
      } else {
        return "";
      }

      // Check if the date is valid
      if (isNaN(date.getTime())) {
        console.warn("Invalid date:", dateString);
        return dateString; // Return original string if invalid
      }

      // Format as DD.MM.YYYY
      return date.toLocaleDateString("de-DE", {
        day: "2-digit",
        month: "2-digit",
        year: "numeric",
      });
    } catch (error) {
      console.error("Error formatting date:", dateString, error);
      return dateString; // Return original string if error
    }
  };

  // Decode HTML entities in strings
  const decodeHtmlEntities = (str) => {
    if (!str) return "";
    return str
      .replace(/&#039;/g, "'")
      .replace(/&quot;/g, '"')
      .replace(/&amp;/g, "&")
      .replace(/&lt;/g, "<")
      .replace(/&gt;/g, ">")
      .replace(/&nbsp;/g, " ")
      .replace(/&#x27;/g, "'")
      .replace(/&#x2F;/g, "/")
      .replace(/&#x60;/g, "`")
      .replace(/&#x3D;/g, "=");
  };

  // Export to PDF
  const exportToPDF = async () => {
    if (!selectedRows || selectedRows.length === 0) {
      alert("Please select rows to export!");
      return;
    }

    const doc = new jsPDF();
    let currentPage = 1;

    for (let index = 0; index < selectedRows.length; index++) {
      const student = selectedRows[index];

      if (index > 0) {
        doc.addPage();
        currentPage++;
      }

      // Add student information in the specified format
      const studentInfo = [
        `Ism: ${decodeHtmlEntities(student.firstname)} ${decodeHtmlEntities(
          student.lastname
        )}`,
        `Tug'ilgan sana: ${formatDate(student.birthdate)}`,
        `Manzil: ${decodeHtmlEntities(student.address)}`,
        `Email: ${decodeHtmlEntities(student.email)}`,
        `Telefon: ${decodeHtmlEntities(student.phone)}`,
        `Institut: ${decodeHtmlEntities(student.institution)}`,
        `Fakultet: ${decodeHtmlEntities(student.faculty)}`,
        `Bosqich: ${decodeHtmlEntities(student.level)}`,
      ];

      // Set font and position
      doc.setFontSize(14);
      doc.setFont("helvetica", "bold");
      doc.text(`Student ${index + 1}`, 20, 30);

      doc.setFontSize(12);
      doc.setFont("helvetica", "normal");

      // Add student information
      studentInfo.forEach((info, i) => {
        doc.text(info, 20, 50 + i * 10);
      });

      // Add images if they exist
      let imageY = 140;

      // Add student photo
      if (student.picture_url) {
        try {
          // Try different URL patterns
          let photoUrl = `${mainUrl}/images/${student.picture_url}`;

          // Check if the URL is already complete
          if (student.picture_url.startsWith("http")) {
            photoUrl = student.picture_url;
          } else if (student.picture_url.startsWith("/")) {
            photoUrl = `${mainUrl}${student.picture_url}`;
          }

          console.log("Attempting to load photo from:", photoUrl);

          const photoResponse = await fetch(photoUrl);

          if (!photoResponse.ok) {
            throw new Error(`HTTP error! status: ${photoResponse.status}`);
          }

          const photoBlob = await photoResponse.blob();

          if (photoBlob.size === 0) {
            throw new Error("Empty file blob");
          }

          // Check if it's an image file
          if (
            student.picture_url.includes(".jpg") ||
            student.picture_url.includes(".jpeg") ||
            student.picture_url.includes(".png")
          ) {
            const photoBase64 = await new Promise((resolve, reject) => {
              const reader = new FileReader();
              reader.onloadend = () => resolve(reader.result);
              reader.onerror = () => reject(new Error("Failed to read image"));
              reader.readAsDataURL(photoBlob);
            });

            doc.addImage(photoBase64, "JPEG", 20, imageY, 40, 50);
            doc.text("Student rasmi:", 70, imageY + 25);
            imageY += 70;
          } else if (student.picture_url.includes(".pdf")) {
            // For PDF files, just show the URL and note that it's a PDF
            doc.text("Student rasmi (PDF):", 20, imageY + 25);
            doc.text(`URL: ${photoUrl}`, 20, imageY + 35);
            imageY += 50;
          } else {
            // For other file types
            doc.text("Student rasmi (fayl):", 20, imageY + 25);
            doc.text(`URL: ${photoUrl}`, 20, imageY + 35);
            imageY += 50;
          }
        } catch (error) {
          console.error("Error loading student photo:", error);
          doc.text(
            `Student rasmi: mavjud emas (${error.message})`,
            20,
            imageY + 25
          );
          imageY += 30;
        }
      } else {
        doc.text("Student rasmi: mavjud emas", 20, imageY + 25);
        imageY += 30;
      }

      // Add passport image
      if (student.passport_url) {
        try {
          // Try different URL patterns
          let passportUrl = `${mainUrl}/images/${student.passport_url}`;

          // Check if the URL is already complete
          if (student.passport_url.startsWith("http")) {
            passportUrl = student.passport_url;
          } else if (student.passport_url.startsWith("/")) {
            passportUrl = `${mainUrl}${student.passport_url}`;
          }

          console.log("Attempting to load passport from:", passportUrl);

          const passportResponse = await fetch(passportUrl);

          if (!passportResponse.ok) {
            throw new Error(`HTTP error! status: ${passportResponse.status}`);
          }

          const passportBlob = await passportResponse.blob();

          if (passportBlob.size === 0) {
            throw new Error("Empty file blob");
          }

          // Check if it's an image file
          if (
            student.passport_url.includes(".jpg") ||
            student.passport_url.includes(".jpeg") ||
            student.passport_url.includes(".png")
          ) {
            const passportBase64 = await new Promise((resolve, reject) => {
              const reader = new FileReader();
              reader.onloadend = () => resolve(reader.result);
              reader.onerror = () => reject(new Error("Failed to read image"));
              reader.readAsDataURL(passportBlob);
            });

            doc.addImage(passportBase64, "JPEG", 20, imageY, 40, 50);
            doc.text("Pasport:", 70, imageY + 25);
          } else if (student.passport_url.includes(".pdf")) {
            // For PDF files, just show the URL and note that it's a PDF
            doc.text("Pasport (PDF):", 20, imageY + 25);
            doc.text(`URL: ${passportUrl}`, 20, imageY + 35);
          } else {
            // For other file types
            doc.text("Pasport (fayl):", 20, imageY + 25);
            doc.text(`URL: ${passportUrl}`, 20, imageY + 35);
          }
        } catch (error) {
          console.error("Error loading passport image:", error);
          doc.text(`Pasport: mavjud emas (${error.message})`, 20, imageY + 25);
        }
      } else {
        doc.text("Pasport: mavjud emas", 20, imageY + 25);
      }

      // Add page number
      doc.setFontSize(10);
      doc.text(`Page ${currentPage}`, 20, 280);
    }

    // Save the PDF
    doc.save("students_data.pdf");
    handleExportClose();
  };

  // Export to Excel
  const exportToExcel = () => {
    if (!selectedRows || selectedRows.length === 0) {
      alert("Please select rows to export!");
      return;
    }

    // Prepare data for Excel
    const excelData = selectedRows.map((student, index) => ({
      "Nr.": index + 1,
      "Registratsiya sanasi": formatDate(student.registration_date),
      Ism: decodeHtmlEntities(student.firstname),
      Familiya: decodeHtmlEntities(student.lastname),
      "Tug'ilgan sana": formatDate(student.birthdate),
      Universitet: decodeHtmlEntities(student.institution),
      Fakultet: decodeHtmlEntities(student.faculty),
      Kurs: decodeHtmlEntities(student.level),
      Email: decodeHtmlEntities(student.email),
      Telefon: decodeHtmlEntities(student.phone),
      "Yashash manzil": decodeHtmlEntities(student.address),
      Status: student.status == 1 ? "Aktiv" : "Aktiv emas",
      "Rasm URL": decodeHtmlEntities(student.picture_url),
      "Passport URL": decodeHtmlEntities(student.passport_url),
    }));

    // Create workbook and worksheet
    const wb = XLSX.utils.book_new();
    const ws = XLSX.utils.json_to_sheet(excelData);

    // Set column widths
    const colWidths = [
      { wch: 5 }, // Nr.
      { wch: 15 }, // Registratsiya sanasi
      { wch: 15 }, // Ism
      { wch: 15 }, // Familiya
      { wch: 12 }, // Tug'ilgan sana
      { wch: 20 }, // Universitet
      { wch: 20 }, // Fakultet
      { wch: 10 }, // Kurs
      { wch: 25 }, // Email
      { wch: 15 }, // Telefon
      { wch: 30 }, // Yashash manzil
      { wch: 10 }, // Status
      { wch: 30 }, // Rasm URL
      { wch: 30 }, // Passport URL
    ];
    ws["!cols"] = colWidths;

    // Add worksheet to workbook
    XLSX.utils.book_append_sheet(wb, ws, "Talabalar");

    // Save the file
    XLSX.writeFile(wb, "students_data.xlsx");
    handleExportClose();
  };

  // Export to Word
  const exportToWord = async () => {
    if (!selectedRows || selectedRows.length === 0) {
      alert("Please select rows to export!");
      return;
    }

    const children = [];

    for (let index = 0; index < selectedRows.length; index++) {
      const student = selectedRows[index];

      if (index > 0) {
        children.push(new PageBreak());
      }

      // Add student title - simplified without heading level
      children.push(
        new Paragraph({
          children: [
            new TextRun({
              text: `Student ${index + 1}`,
              bold: true,
              size: 28,
            }),
          ],
          spacing: { after: 300 },
        })
      );

      // Add student information in the specified format
      const studentInfo = [
        `Ism: ${decodeHtmlEntities(student.firstname)} ${decodeHtmlEntities(
          student.lastname
        )}`,
        `Tug'ilgan sana: ${formatDate(student.birthdate)}`,
        `Manzil: ${decodeHtmlEntities(student.address)}`,
        `Email: ${decodeHtmlEntities(student.email)}`,
        `Telefon: ${decodeHtmlEntities(student.phone)}`,
        `Institut: ${decodeHtmlEntities(student.institution)}`,
        `Fakultet: ${decodeHtmlEntities(student.faculty)}`,
        `Bosqich: ${decodeHtmlEntities(student.level)}`,
      ];

      // Add student information with simplified formatting
      studentInfo.forEach((info) => {
        children.push(
          new Paragraph({
            children: [
              new TextRun({
                text: info,
                size: 22,
              }),
            ],
            spacing: { after: 150 },
          })
        );
      });

      // Add images if they exist - simplified approach
      const imageParagraphs = [];

      // Add student photo
      if (student.picture_url) {
        try {
          // Try different URL patterns
          let photoUrl = `${mainUrl}/images/${student.picture_url}`;

          // Check if the URL is already complete
          if (student.picture_url.startsWith("http")) {
            photoUrl = student.picture_url;
          } else if (student.picture_url.startsWith("/")) {
            photoUrl = `${mainUrl}${student.picture_url}`;
          }

          console.log("Attempting to load photo from:", photoUrl);

          const photoResponse = await fetch(photoUrl);

          if (!photoResponse.ok) {
            throw new Error(`HTTP error! status: ${photoResponse.status}`);
          }

          const photoArrayBuffer = await photoResponse.arrayBuffer();

          if (photoArrayBuffer.byteLength === 0) {
            throw new Error("Empty file array buffer");
          }

          // Check if it's an image file
          if (
            student.picture_url.includes(".jpg") ||
            student.picture_url.includes(".jpeg") ||
            student.picture_url.includes(".png")
          ) {
            imageParagraphs.push(
              new Paragraph({
                children: [
                  new TextRun({
                    text: "Student rasmi:",
                    bold: true,
                    size: 22,
                  }),
                ],
                spacing: { after: 150 },
              }),
              new Paragraph({
                children: [
                  new ImageRun({
                    data: photoArrayBuffer,
                    transformation: {
                      width: 120,
                      height: 160,
                    },
                  }),
                ],
                spacing: { after: 300 },
              })
            );
          } else {
            // For PDF and other file types, just show the URL
            imageParagraphs.push(
              new Paragraph({
                children: [
                  new TextRun({
                    text: student.picture_url.includes(".pdf")
                      ? "Student rasmi (PDF):"
                      : "Student rasmi (fayl):",
                    bold: true,
                    size: 22,
                  }),
                ],
                spacing: { after: 150 },
              }),
              new Paragraph({
                children: [
                  new TextRun({
                    text: `URL: ${photoUrl}`,
                    size: 20,
                  }),
                ],
                spacing: { after: 300 },
              })
            );
          }
        } catch (error) {
          console.error("Error loading student photo:", error);
          imageParagraphs.push(
            new Paragraph({
              children: [
                new TextRun({
                  text: `Student rasmi: mavjud emas (${error.message})`,
                  size: 22,
                }),
              ],
              spacing: { after: 150 },
            })
          );
        }
      } else {
        imageParagraphs.push(
          new Paragraph({
            children: [
              new TextRun({
                text: "Student rasmi: mavjud emas",
                size: 22,
              }),
            ],
            spacing: { after: 150 },
          })
        );
      }

      // Add passport image
      if (student.passport_url) {
        try {
          // Try different URL patterns
          let passportUrl = `${mainUrl}/images/${student.passport_url}`;

          // Check if the URL is already complete
          if (student.passport_url.startsWith("http")) {
            passportUrl = student.passport_url;
          } else if (student.passport_url.startsWith("/")) {
            passportUrl = `${mainUrl}${student.passport_url}`;
          }

          console.log("Attempting to load passport from:", passportUrl);

          const passportResponse = await fetch(passportUrl);

          if (!passportResponse.ok) {
            throw new Error(`HTTP error! status: ${passportResponse.status}`);
          }

          const passportArrayBuffer = await passportResponse.arrayBuffer();

          if (passportArrayBuffer.byteLength === 0) {
            throw new Error("Empty file array buffer");
          }

          // Check if it's an image file
          if (
            student.passport_url.includes(".jpg") ||
            student.passport_url.includes(".jpeg") ||
            student.passport_url.includes(".png")
          ) {
            imageParagraphs.push(
              new Paragraph({
                children: [
                  new TextRun({
                    text: "Passport:",
                    bold: true,
                    size: 22,
                  }),
                ],
                spacing: { after: 150 },
              }),
              new Paragraph({
                children: [
                  new ImageRun({
                    data: passportArrayBuffer,
                    transformation: {
                      width: 120,
                      height: 160,
                    },
                  }),
                ],
                spacing: { after: 300 },
              })
            );
          } else {
            // For PDF and other file types, just show the URL
            imageParagraphs.push(
              new Paragraph({
                children: [
                  new TextRun({
                    text: student.passport_url.includes(".pdf")
                      ? "Passport (PDF):"
                      : "Passport (fayl):",
                    bold: true,
                    size: 22,
                  }),
                ],
                spacing: { after: 150 },
              }),
              new Paragraph({
                children: [
                  new TextRun({
                    text: `URL: ${passportUrl}`,
                    size: 20,
                  }),
                ],
                spacing: { after: 300 },
              })
            );
          }
        } catch (error) {
          console.error("Error loading passport image:", error);
          imageParagraphs.push(
            new Paragraph({
              children: [
                new TextRun({
                  text: `Pasport: mavjud emas (${error.message})`,
                  size: 22,
                }),
              ],
              spacing: { after: 150 },
            })
          );
        }
      } else {
        imageParagraphs.push(
          new Paragraph({
            children: [
              new TextRun({
                text: "Pasport: mavjud emas",
                size: 22,
              }),
            ],
            spacing: { after: 150 },
          })
        );
      }

      // Add all image paragraphs to children
      children.push(...imageParagraphs);
    }

    // Create a simpler document structure
    const doc = new Document({
      sections: [
        {
          properties: {},
          children: children,
        },
      ],
    });

    // Generate and save the document
    const blob = await Packer.toBlob(doc);
    saveAs(blob, "students_data.docx");
    handleExportClose();
  };

  const columns = [
    {
      title: "Nr.",
      editable: "never",
      render: (rowData, type, row) => {
        return rowData.tableData?.id !== undefined
          ? rowData.tableData.id + 1
          : "";
      },
    },
    {
      field: "registration_date",
      title: "Registratsiya sanasi",
      editable: "never",
      render: (rowData) => {
        return formatDate(rowData.registration_date);
      },
    },
    {
      field: "picture_url",
      title: "Rasm",
      editable: "never",

      render: (rowData) => {
        const imageClick = () => {
          console.log("Click");
        };

        if (
          rowData.picture_url.includes(".jpg") ||
          rowData.picture_url.includes(".jpeg") ||
          rowData.picture_url.includes(".png")
        ) {
          return (
            <a
              href={`${mainUrl}/images/${rowData.picture_url}`}
              target="_blank"
              rel="noreferrer"
            >
              <img
                src={`${mainUrl}/images/${rowData.picture_url}`}
                height="100%"
                width={40}
                alt=""
                style={{ cursor: "pointer", zIndex: "999" }}
                onClick={imageClick}
              />
            </a>
          );
        } else if (
          rowData.picture_url.includes(".pdf") ||
          rowData.picture_url.includes(".doc") ||
          rowData.picture_url.includes(".docx")
        ) {
          return (
            <a
              href={`${mainUrl}/images/${rowData.picture_url}`}
              target="_blank"
              rel="noreferrer"
            >
              rasm.pdf
            </a>
          );
        }
      },
    },
    {
      field: "passport_url",
      title: "Passport",
      editable: "never",
      render: (rowData) => {
        if (
          rowData.passport_url.includes(".jpg") ||
          rowData.passport_url.includes(".jpeg") ||
          rowData.passport_url.includes(".png")
        ) {
          return (
            <a
              href={`${mainUrl}/images/${rowData.passport_url}`}
              target="_blank"
              rel="noreferrer"
            >
              <img
                src={`${mainUrl}/images/${rowData.passport_url}`}
                height="100%"
                width={40}
                alt=""
                style={{ cursor: "pointer", zIndex: "999" }}
              />
            </a>
          );
        } else if (rowData.passport_url.includes(".pdf")) {
          return (
            <a
              href={`${mainUrl}/images/${rowData.passport_url}`}
              target="_blank"
              rel="noreferrer"
            >
              rasm.pdf
            </a>
          );
        }
      },
    },
    {
      field: "firstname",
      title: "Ism",
      editable: "never",
    },
    {
      field: "lastname",
      title: "Familiya",
      editable: "never",
    },
    {
      field: "birthdate",
      title: "Tug'ilgan sana",
      editable: "never",
      render: (rowData) => {
        return formatDate(rowData.birthdate);
      },
    },
    {
      field: "institution",
      title: "Universitet",
      editable: "never",
    },
    {
      field: "faculty",
      title: "Fakultet",
      editable: "never",
    },
    {
      field: "level",
      title: "Kurs",
      editable: "never",
    },

    {
      field: "email",
      title: "Email",
      editable: "never",
    },
    {
      field: "phone",
      title: "Telefon",
      editable: "never",
    },

    {
      field: "address",
      title: "Yashash manzil",
      editable: "never",
    },
    {
      field: "status",
      title: "Status",
      render: (rowData) => {
        if (rowData.status == 1) {
          return <div>Aktiv</div>;
        } else {
          return <div>Aktiv emas</div>;
        }
      },
    },
  ];

  return (
    <div className="export-students">
      <Box style={{ minHeight: "300px", margin: "0 20px" }}>
        <Box className="section_container">
          {/* <Box className="title">
            {data ? <div>Ro'yxat</div> : <div>Ro'yxat mavjud emas!</div>}
          </Box> */}

          <ThemeProvider theme={defaultMaterialTheme}>
            <MaterialTable
              title="Talabalar ro'yxati"
              columns={columns}
              data={data}
              loading={loading}
              icons={tableIcons}
              options={{
                // grouping: true,
                search: true,
                sorting: true,
                actionsColumnIndex: 0,
                paging: true,
                pageSize: 10, // make initial page size
                emptyRowsWhenPaging: false, // To avoid of having empty rows
                pageSizeOptions: [10, 20, 30, 50], // rows selection options
                headerStyle: {
                  backgroundColor: "#3c5132",
                  color: "#FFF",
                },
                minBodyHeight: window.innerHeight - 300 + "px",
                selection: true,
                showSelectAllCheckbox: true,
                showSelectionIcons: true,
                selectionProps: (rowData) => ({
                  disabled: false,
                }),
              }}
              onSelectionChange={(rows) => {
                setSelectedRows(rows);
              }}
              components={{
                Toolbar: (props) => (
                  <div
                    style={{
                      display: "flex",
                      justifyContent: "space-between",
                      alignItems: "center",
                      padding: "8px 16px",
                    }}
                  >
                    <div style={{ display: "flex", alignItems: "center" }}>
                      {props.components.Actions}
                    </div>
                    <div
                      style={{
                        display: "flex",
                        alignItems: "center",
                        gap: "8px",
                      }}
                    >
                      <Button
                        variant="outlined"
                        onClick={getRegisteredPeople}
                        sx={{
                          textTransform: "none",
                          fontSize: "0.875rem",
                        }}
                      >
                        Refresh
                      </Button>
                      <Button
                        variant="contained"
                        onClick={handleExportClick}
                        sx={{
                          backgroundColor: "#3c5132",
                          color: "white",
                          "&:hover": { backgroundColor: "#4caf50" },
                          textTransform: "none",
                          fontSize: "0.875rem",
                        }}
                      >
                        Export{" "}
                        {selectedRows.length > 0
                          ? `(${selectedRows.length} selected)`
                          : ""}
                      </Button>
                    </div>
                  </div>
                ),
              }}
              localization={{
                body: {
                  emptyDataSourceMessage: "Keine Einträge",
                  addTooltip: "Hinzufügen",
                  deleteTooltip: "Löschen",
                  editTooltip: "Bearbeiten",
                  filterRow: {
                    filterTooltip: "Filter",
                  },
                  editRow: {
                    deleteText: "Diese Zeile wirklich löschen?",
                    cancelTooltip: "Abbrechen",
                    saveTooltip: "Speichern",
                  },
                },
                grouping: {
                  placeholder: "Spalten ziehen ...",
                  groupedBy: "Gruppiert nach:",
                },
                header: {
                  actions: "Aktionen",
                },
                pagination: {
                  labelDisplayedRows: "{from}-{to} von {count}",
                  labelRowsSelect: "Zeilen",
                  labelRowsPerPage: "Zeilen pro Seite:",
                  firstAriaLabel: "Erste Seite",
                  firstTooltip: "Erste Seite",
                  previousAriaLabel: "Vorherige Seite",
                  previousTooltip: "Vorherige Seite",
                  nextAriaLabel: "Nächste Seite",
                  nextTooltip: "Nächste Seite",
                  lastAriaLabel: "Letzte Seite",
                  lastTooltip: "Letzte Seite",
                },
                toolbar: {
                  addRemoveColumns: "Spalten hinzufügen oder löschen",
                  nRowsSelected: "{0} Zeile(n) ausgewählt",
                  showColumnsTitle: "Zeige Spalten",
                  showColumnsAriaLabel: "Zeige Spalten",
                  exportTitle: "Export",
                  exportAriaLabel: "Export",
                  exportName: "Export als CSV",
                  searchTooltip: "Suche",
                  searchPlaceholder: "Suche",
                },
              }}
              editable={{
                // onRowUpdate: (newData, oldData) =>
                //   new Promise((resolve, reject) => {
                //     setTimeout(() => {
                //       const dataUpdate = [...data];
                //       const index = oldData.tableData.id;
                //       dataUpdate[index] = newData;
                //       setData([...dataUpdate]);

                //       resolve();
                //     }, 1000);
                //   }),
                onRowDelete: (oldData) =>
                  new Promise((resolve, reject) => {
                    setTimeout(() => {
                      const dataDelete = [...data];
                      const index = oldData.tableData.id;
                      dataDelete.splice(index, 1);
                      setData([...dataDelete]);
                      deletePerson(oldData);

                      resolve();
                    }, 1000);
                  }),
              }}
            />
          </ThemeProvider>

          {/* Export Menu */}
          <Menu
            anchorEl={exportAnchorEl}
            open={Boolean(exportAnchorEl)}
            onClose={handleExportClose}
            PaperProps={{
              sx: {
                mt: 1,
                minWidth: 200,
                boxShadow: "0 4px 20px rgba(0,0,0,0.15)",
                borderRadius: 2,
              },
            }}
          >
            <MenuItem
              onClick={exportToPDF}
              disabled={selectedRows.length === 0}
              sx={{
                opacity: selectedRows.length === 0 ? 0.5 : 1,
                cursor: selectedRows.length === 0 ? "not-allowed" : "pointer",
              }}
            >
              <ListItemIcon>
                <PictureAsPdf
                  sx={{ color: selectedRows.length === 0 ? "#ccc" : "#d32f2f" }}
                />
              </ListItemIcon>
              <ListItemText
                primary={`Export as PDF ${
                  selectedRows.length > 0
                    ? `(${selectedRows.length} selected)`
                    : "(No rows selected)"
                }`}
              />
            </MenuItem>
            <MenuItem
              onClick={exportToWord}
              disabled={selectedRows.length === 0}
              sx={{
                opacity: selectedRows.length === 0 ? 0.5 : 1,
                cursor: selectedRows.length === 0 ? "not-allowed" : "pointer",
              }}
            >
              <ListItemIcon>
                <Description
                  sx={{ color: selectedRows.length === 0 ? "#ccc" : "#1976d2" }}
                />
              </ListItemIcon>
              <ListItemText
                primary={`Export as Word ${
                  selectedRows.length > 0
                    ? `(${selectedRows.length} selected)`
                    : "(No rows selected)"
                }`}
              />
            </MenuItem>
            <MenuItem
              onClick={exportToExcel}
              disabled={selectedRows.length === 0}
              sx={{
                opacity: selectedRows.length === 0 ? 0.5 : 1,
                cursor: selectedRows.length === 0 ? "not-allowed" : "pointer",
              }}
            >
              <ListItemIcon>
                <TableChart
                  sx={{ color: selectedRows.length === 0 ? "#ccc" : "#2e7d32" }}
                />
              </ListItemIcon>
              <ListItemText
                primary={`Export as Excel ${
                  selectedRows.length > 0
                    ? `(${selectedRows.length} selected)`
                    : "(No rows selected)"
                }`}
              />
            </MenuItem>
          </Menu>
        </Box>
      </Box>
    </div>
  );
};

export default RegisteredPeople;
